import styled from "@emotion/styled";
import {
  FilterAltOutlined,
  Search,
  VisibilityOutlined
} from "@mui/icons-material";
import {
  Box,
  Button,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@mui/material";
import queryString from "query-string";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useDebounce, useSearchParam } from "react-use";
import {
  FILTER_OPERATIONS,
  MEMBER_STATES,
  SEARCH_PLACEHOLDERS,
  TAB_NAMES
} from "../constants";
import { useMembersStore } from "../stores";

const StyledTab = styled(Tab)`
  color: white;

  &.Mui-selected {
    border-radius: 5px;
    background-color: white;
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

export function MembersTableHeader({ isFilterMode = false }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedValue, setDebouncedValue] = useState("");
  const queries = useMembersStore(state => state.queries);
  const setQueries = useMembersStore(state => state.setQueries);
  const openColumnsVisibilityModal = useMembersStore(
    state => state.openColumnsVisibilityModal
  );
  const currentTab = useMembersStore(state => state.currentTab);
  const setCurrentTab = useMembersStore(state => state.setCurrentTab);

  const location = useLocation();
  const queryParams = queryString.parse(location.search);
  const query = queryString.stringify(queryParams);

  const tabParam = useSearchParam("t");

  useEffect(() => {
    switch (tabParam) {
      case "members":
        setCurrentTab(TAB_NAMES.MEMBERS);
        break;

      case "inactive":
        setCurrentTab(TAB_NAMES.INACTIVE_MEMBERS);
        break;

      case "kids":
        setCurrentTab(TAB_NAMES.KIDS);
        break;

      case "partners":
        setCurrentTab(TAB_NAMES.PARTNERS);
        break;

      default:
        setCurrentTab(TAB_NAMES.APPLICATION_REQUESTS);
    }
  }, [tabParam]);

  useDebounce(
    () => {
      setDebouncedValue(searchTerm);
    },
    500,
    [searchTerm]
  );

  useEffect(() => {
    if (debouncedValue) {
      setQueries({
        ...queries,
        search: debouncedValue
      });
    } else {
      if (currentTab === TAB_NAMES.MEMBERS) {
        setQueries(
          JSON.stringify(queryParams.queries) !== undefined
            ? JSON.parse(queryParams.queries)
            : {
                filter: {
                  status: [
                    {
                      operator: FILTER_OPERATIONS.EQUAL,
                      value: MEMBER_STATES.ACTIVE
                    }
                  ]
                }
              }
        );
      }

      if (currentTab === TAB_NAMES.APPLICATION_REQUESTS) {
        setQueries(
          JSON.stringify(queryParams.queries) !== undefined
            ? JSON.parse(queryParams.queries)
            : {
                filter: {
                  status: [
                    {
                      operator: FILTER_OPERATIONS.NOT_EQUAL,
                      value: MEMBER_STATES.ACTIVE
                    },
                    {
                      operator: FILTER_OPERATIONS.NOT_EQUAL,
                      value: MEMBER_STATES.INACTIVE
                    }
                  ]
                }
              }
        );
      }

      if (currentTab === TAB_NAMES.INACTIVE_MEMBERS) {
        setQueries(
          JSON.stringify(queryParams.queries) !== undefined
            ? JSON.parse(queryParams.queries)
            : {
                filter: {
                  status: [
                    {
                      operator: FILTER_OPERATIONS.EQUAL,
                      value: MEMBER_STATES.INACTIVE
                    }
                  ]
                }
              }
        );
      }

      if (currentTab === TAB_NAMES.KIDS) {
        setQueries(
          JSON.stringify(queryParams.queries) !== undefined
            ? JSON.parse(queryParams.queries)
            : {
                filter: {}
              }
        );
      }

      if (currentTab === TAB_NAMES.PARTNERS) {
        setQueries(
          JSON.stringify(queryParams.queries) !== undefined
            ? JSON.parse(queryParams.queries)
            : {}
        );
      }
    }
  }, [debouncedValue, currentTab]);

  return (
    <Box
      sx={{
        color: "white",
        backgroundColor: "primary.main",
        py: 1,
        px: 2,
        borderRadius: "5px"
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={1}
      >
        <Tabs
          value={currentTab}
          onChange={(event, value) => {
            setCurrentTab(value);
            let newQueries = {};

            if (value === TAB_NAMES.MEMBERS) {
              newQueries = {
                filter: {
                  status: [
                    {
                      operator: FILTER_OPERATIONS.EQUAL,
                      value: MEMBER_STATES.ACTIVE
                    }
                  ]
                }
              };
              navigate(
                `?t=members&queries=${encodeURIComponent(
                  JSON.stringify(newQueries)
                )}`
              );
            }

            if (value === TAB_NAMES.APPLICATION_REQUESTS) {
              newQueries = {
                filter: {
                  status: [
                    {
                      operator: FILTER_OPERATIONS.NOT_EQUAL,
                      value: MEMBER_STATES.ACTIVE
                    },
                    {
                      operator: FILTER_OPERATIONS.NOT_EQUAL,
                      value: MEMBER_STATES.INACTIVE
                    }
                  ]
                }
              };
              navigate(
                `?t=applicationRequests&queries=${encodeURIComponent(
                  JSON.stringify(newQueries)
                )}`
              );
            }

            if (value === TAB_NAMES.INACTIVE_MEMBERS) {
              newQueries = {
                filter: {
                  status: [
                    {
                      operator: FILTER_OPERATIONS.EQUAL,
                      value: MEMBER_STATES.INACTIVE
                    }
                  ]
                }
              };
              navigate(
                `?t=inactive&queries=${encodeURIComponent(
                  JSON.stringify(newQueries)
                )}`
              );
            }

            if (value === TAB_NAMES.KIDS) {
              newQueries = {
                filter: {}
              };
              navigate(
                `?t=kids&queries=${encodeURIComponent(
                  JSON.stringify(newQueries)
                )}`
              );
            }

            if (value === TAB_NAMES.PARTNERS) {
              newQueries = {};
              navigate(
                `?t=partners&queries=${encodeURIComponent(
                  JSON.stringify(newQueries)
                )}`
              );
            }

            setQueries(newQueries);
          }}
          indicatorColor=""
          variant="scrollable"
          scrollButtons="auto"
        >
          <StyledTab
            label={t("members.membersHeader.applicationRequest")}
            value="applicationRequests"
          />
          <StyledTab
            label={t("members.membersHeader.members")}
            value="members"
          />
          <StyledTab
            label={t("members.membersHeader.inactiveMembers")}
            value="inactiveMembers"
          />
          <StyledTab label={t("members.membersHeader.kids")} value="kids" />
          <StyledTab
            label={t("members.membersHeader.partners")}
            value="partners"
          />
        </Tabs>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          justifyContent={{ xs: "flex-start", md: "flex-end" }}
        >
          <TextField
            id="outlined-basic"
            variant="outlined"
            size="small"
            sx={{ width: { xs: "100%", sm: "auto" } }}
            placeholder={`${t("addMembers.step3.Search")} ${t(
              SEARCH_PLACEHOLDERS[currentTab]
            )}`}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search color="primary" />
                </InputAdornment>
              ),
              sx: {
                px: 1,
                borderRadius: "50px",
                backgroundColor: "white",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none"
                },
                "&.Mui-focused": {
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none"
                  }
                }
              }
            }}
            onChange={e => setSearchTerm(e.target.value)}
          />
          {!isFilterMode && (
            <Button
              variant="text"
              color="secondary"
              startIcon={<FilterAltOutlined />}
              onClick={() => navigate(`/members/filter?${query}`)}
            >
              <Typography variant="subtitle2">
                {t("members.membersHeader.filter")}
              </Typography>
            </Button>
          )}
          <Button
            variant="text"
            color="secondary"
            startIcon={<VisibilityOutlined />}
            onClick={openColumnsVisibilityModal}
          >
            <Typography variant="subtitle2">
              {t("members.membersHeader.view")}
            </Typography>
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
